/**
 * What every page beside the agents screen is built from: the page frame, an empty state, the bar of
 * tabs under the top bar, a ticker tile, a worker's levels on one line, and a choice said in words.
 */
import type { ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { create } from 'zustand'
import { fillComposer } from './Agents'
import { Menu, cx } from './kit'
import { useDemo } from './store'

export const Page = ({ children, className }: { children: ReactNode; className?: string }) => <div className={cx('w-full px-4 pb-10 pt-5 md:px-10 md:pt-8', className)}>{children}</div>

/** A page with nothing on it yet: what is missing, in a sentence, and the one way forward. */
export function Empty({ icon, title, children, action }: { icon?: ReactNode; title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center px-6 pb-[12vh] pt-10 text-center">
      {icon}
      <h1 className="m-0 mt-4 font-ws-serif text-[28px] font-normal leading-9 tracking-[-0.01em] text-ws-fg">{title}</h1>
      <p className="m-0 mt-3 max-w-[420px] text-[13px] leading-5 tracking-normal text-ws-fg-3">{children}</p>
      {action && <div className="mt-6 flex items-center gap-2">{action}</div>}
    </div>
  )
}

/** The bar under the top bar: tabs (or a way back) on the left, the page's own actions on the right. */
export function Bar({ children, right }: { children?: ReactNode; right?: ReactNode }) {
  return (
    <div className="flex h-12 shrink-0 items-center gap-2 border-b border-ws-line pl-2 pr-2 md:pr-4">
      <div className="flex min-w-0 flex-1 items-center">{children}</div>
      {right && <div className="flex shrink-0 items-center">{right}</div>}
    </div>
  )
}

/** What each page's tabs are set to (kept while you move between pages). */
export const useTabs = create<{ account: string; workers: string; apps: string; settings: string; set: (p: Record<string, unknown>) => void }>((set) => ({
  account: 'all',
  workers: 'running',
  apps: 'all',
  settings: 'general',
  set: (p) => set(p as never),
}))

/** Take words to an agent: open its empty page with them already in the composer. */
export const ask = (agentId: string | null | undefined, text: string) => {
  const s = useDemo.getState()
  const id = s.agents.some((a) => a.id === agentId) ? agentId! : (s.agents.find((a) => a.kind === 'lead')?.id ?? s.agents[0]?.id ?? '')
  if (!id) return
  s.openSession(id, null)
  window.setTimeout(() => fillComposer(text), 60)
}

/** A stock's mark where there is no logo: its letters on a tile. */
export const Ticker = ({ symbol }: { symbol: string }) => (
  <span className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-[6px] bg-ws-bg px-[6px] text-[10.5px] font-semibold leading-none tracking-[0.02em] text-ws-fg-2" aria-hidden>
    {symbol}
  </span>
)

/** A count of shares: whole when it is whole, otherwise as many decimals as it has. */
export const qtyText = (n: number) => (Number.isInteger(n) ? `${n}` : n.toFixed(6).replace(/0+$/, '').replace(/\.$/, ''))

/** A choice from a list, said in words — opens a menu. */
export function Choose<T extends string>({ value, options, onChange, align = 'right', width = 220 }: { value: T; options: readonly (readonly [T, string, string?])[]; onChange: (v: T) => void; align?: 'left' | 'right'; width?: number }) {
  return (
    <Menu
      align={align}
      width={width}
      items={options.map(([id, label, hint]) => ({ label, hint: hint && width > 260 ? hint : undefined, checked: id === value, onSelect: () => onChange(id) }))}
      trigger={() => (
        <button type="button" className="flex h-7 max-w-full items-center gap-1 rounded-[6px] px-2 text-[13px] tracking-normal text-ws-fg-2 transition-colors duration-150 hover:bg-ws-raise hover:text-ws-fg">
          <span className="truncate">{options.find((o) => o[0] === value)?.[1]}</span>
          <ChevronDown size={13} strokeWidth={2} className="shrink-0 text-ws-fg-4" />
        </button>
      )}
    />
  )
}

/** A worker's levels on one line: where it stops, where it came in, where the price is, where it sells. */
export function Ladder({ stop, entry, last, target }: { stop: number | null; entry: number; last: number; target: number | null }) {
  const marks = [
    { k: 'Stop', v: stop, cls: 'text-ws-down' },
    { k: 'Entry', v: entry, cls: 'text-ws-fg-2' },
    { k: 'Last', v: last, cls: 'text-ws-fg' },
    { k: 'Target', v: target, cls: 'text-ws-up' },
  ].filter((m): m is { k: string; v: number; cls: string } => m.v != null)
  const lo = Math.min(...marks.map((m) => m.v))
  const hi = Math.max(...marks.map((m) => m.v))
  const pad = (hi - lo || 1) * 0.06
  const at = (v: number) => `${(((v - (lo - pad)) / (hi - lo + pad * 2)) * 100).toFixed(2)}%`
  const up = last >= entry
  return (
    <div>
      <div className="relative h-4">
        <span className="absolute inset-x-0 top-[7px] h-[2px] bg-ws-edge" />
        <span className={cx('absolute top-[7px] h-[2px]', up ? 'bg-ws-up/60' : 'bg-ws-down/60')} style={{ left: at(Math.min(entry, last)), right: `calc(100% - ${at(Math.max(entry, last))})` }} />
        {stop != null && <span className="absolute top-[3px] h-[10px] w-[2px] -translate-x-px bg-ws-down" style={{ left: at(stop) }} />}
        <span className="absolute top-[3px] h-[10px] w-[2px] -translate-x-px bg-ws-fg-4" style={{ left: at(entry) }} />
        {target != null && <span className="absolute top-[3px] h-[10px] w-[2px] -translate-x-px bg-ws-up" style={{ left: at(target) }} />}
        <span className="absolute top-[4px] h-2 w-2 -translate-x-1/2 rounded-full bg-white shadow-[0_0_0_2px_var(--color-ws-panel)]" style={{ left: at(last) }} />
      </div>
      <dl className="m-0 mt-1 grid" style={{ gridTemplateColumns: `repeat(${marks.length}, minmax(0, 1fr))` }}>
        {marks
          .toSorted((a, b) => a.v - b.v)
          .map(({ k, v, cls }, i) => (
            <div key={k} className={i === 0 ? '' : i === marks.length - 1 ? 'text-right' : 'text-center'}>
              <dt className="text-[11px] leading-4 tracking-normal text-ws-fg-4">{k}</dt>
              <dd className={cx('m-0 text-[12px] leading-4 tracking-normal tabular-nums', cls)}>{v.toFixed(2)}</dd>
            </div>
          ))}
      </dl>
    </div>
  )
}
